import {XIcon} from '@heroicons/react/outline';
import {useCallback, useEffect, useMemo, useState} from 'react';

import Spinner from '@/assets/svg/Spinner';
import {Button, PlusIcon, Select} from '@/components';
import Modal from '@/components/Modals';
import {
  getChannelProcessors,
  getProcessorTxTypeOptions,
  getTransactionChannelsOptions,
  getTransactionProcessorsOptions,
} from '@/constants/formatters';
import type {
  CorridorProcessors,
  ProcessorCapabilityMatrix,
} from '@/services/processorService';
import type {AfriexCurrency, AfriexCurrencyCode} from '@/types';
import {
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

import type {ProcessorSettingTarget, UpdateProcessorRequest} from './types';

interface UpdateProcessorModalProps {
  isOpen: boolean;
  target: ProcessorSettingTarget;
  onClose: () => void;
  /**
   * Async. The only supplier is `useProcessorList`'s `handleUpdateProcessor`,
   * which awaits `currencyService.saveOne`. `handleSubmit` below awaits it to
   * keep the spinner up for the whole save, and that `await` only type-checks
   * against a promise-returning contract; declared `=> void`, the await was
   * awaiting `undefined` and the declaration was simply wrong about a
   * corridor-config write.
   */
  handleUpdate: (data: UpdateProcessorRequest) => Promise<void>;
  processors: AfriexTransactionProcessor[];
  currency: AfriexCurrency;
  capabilityMatrix: ProcessorCapabilityMatrix;
}

const UpdateProcessorModal = ({
  target,
  isOpen,
  onClose,
  processors,
  currency,
  handleUpdate,
  capabilityMatrix,
}: UpdateProcessorModalProps) => {
  const [transactionType, setTransactionType] = useState<AfriexTransactionType>(
    AfriexTransactionType.DEPOSIT,
  );
  const [transactionChannel, setTransactionChannel] =
    useState<AfriexTransactionChannel>(AfriexTransactionChannel.BANK_ACCOUNT);
  const [processor, setProcessor] = useState<AfriexTransactionProcessor>();
  const [processorsList, setProcessorsList] = useState<
    AfriexTransactionProcessor[]
  >([]);
  const [processorToAdd, setProcessorToAdd] =
    useState<AfriexTransactionProcessor>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // The server reads the widget corridor on the retail surface only
  // (ProcessorInteractor.resolveWidgetLinkProcessor), so it is editable there.
  const excludedChannels = [
    AfriexTransactionChannel.ADMIN,
    AfriexTransactionChannel.WALLET,
    ...(target === 'user' ? [] : [AfriexTransactionChannel.WIDGET]),
  ];

  const handleRemoveFromList = (p: AfriexTransactionProcessor) => {
    setProcessorsList((prev) => prev.filter((x) => x !== p));
  };

  const handleSubmit = async () => {
    const payload: UpdateProcessorRequest = {
      currency,
      target,
      type: transactionType,
      channel: transactionChannel,
      processorName: processor as AfriexTransactionProcessor,
      processors: processorsList,
    };
    setIsLoading(true);
    await handleUpdate(payload);
    setIsLoading(false);
  };

  // Derive what's currently saved in the backend for the selected type+channel
  const backendProcessor = useMemo(
    () =>
      currency?.[target]?.[transactionType]?.[transactionChannel]
        ?.currentProcessor,
    [currency, target, transactionType, transactionChannel],
  );

  const backendProcessorsList = useMemo(
    () =>
      currency?.[target]?.[transactionType]?.[transactionChannel]?.processors ??
      [],
    [currency, target, transactionType, transactionChannel],
  );

  // Derive the set of processors allowed by the capability matrix for the current
  // type + channel + currency combination. Always a Set — empty means "none available".
  const corridorProcessors = useCallback(
    (matrix?: CorridorProcessors) => {
      const typeCapability = matrix?.[transactionType];
      if (!typeCapability) {
        return new Set<string>();
      }
      // getChannelProcessors drops the retail-only rails for the business and
      // api targets, so they never reach either dropdown. The server rejects
      // them on PATCH regardless.
      const match = getChannelProcessors(
        typeCapability,
        currency?.code as AfriexCurrencyCode,
        target,
      ).find(({channel}) => channel === transactionChannel);
      return match?.processors ?? new Set<string>();
    },
    [transactionType, transactionChannel, currency, target],
  );
  const allowedProcessors = useMemo(
    () => corridorProcessors(capabilityMatrix),
    [corridorProcessors, capabilityMatrix],
  );

  // Declared for this corridor but switched off by the server: shown, but not
  // selectable, so it is clear why they cannot be chosen.
  const unavailableOptions = useMemo(() => {
    const disabledOptions = (processors: Set<string>, note: string) =>
      getTransactionProcessorsOptions(
        (
          Object.values(
            AfriexTransactionProcessor,
          ) as AfriexTransactionProcessor[]
        ).filter((p) => !processors.has(p)),
      ).map((option) => ({
        ...option,
        key: `${option.key} (!) ${note}`,
        disabled: true,
      }));
    return [
      ...disabledOptions(
        corridorProcessors(capabilityMatrix?.dev),
        'not live in production',
      ),
      ...disabledOptions(
        corridorProcessors(capabilityMatrix?.retired),
        'retired',
      ),
    ];
  }, [corridorProcessors, capabilityMatrix]);

  // Build dropdown options by excluding everything NOT in allowedProcessors
  const processorOptions = useMemo(() => {
    const disallowed = (
      Object.values(AfriexTransactionProcessor) as AfriexTransactionProcessor[]
    ).filter((p) => !allowedProcessors.has(p));
    return getTransactionProcessorsOptions(disallowed);
  }, [allowedProcessors]);

  // The pending addition has to be something the dropdown is actually showing.
  // It used to be seeded from the full processor list, whose first entry sorts
  // to AFRIEX_API and is almost never corridor-eligible: the field rendered
  // blank while state held a processor nobody had chosen, and Add inserted it.
  const canAddProcessor =
    !!processorToAdd &&
    processorOptions.some((option) => option.value === processorToAdd) &&
    !processorsList.includes(processorToAdd);

  const handleAddToList = () => {
    if (!canAddProcessor || !processorToAdd) {
      return;
    }
    setProcessorsList((prev) => [...prev, processorToAdd]);
  };

  /**
   * Options for "Current Processor", including the stored value even when the
   * capability matrix no longer offers it.
   *
   * A configured processor need not be corridor-eligible: config predates the
   * matrix, or the rail was later restricted for this audience. Options come
   * from the matrix, so without this the field renders blank and hides what is
   * actually configured, which is the worst thing a config screen can do.
   */
  const currentProcessorOptions = useMemo(() => {
    const unavailable = unavailableOptions.filter(
      (option) => option.value !== backendProcessor,
    );
    if (
      !backendProcessor ||
      processorOptions.some((option) => option.value === backendProcessor)
    ) {
      return [...processorOptions, ...unavailable];
    }
    return [
      ...processorOptions,
      {
        key: `${capitalizeWords(
          String(backendProcessor).replace(/_/g, ' '),
        )} (configured, not available)`,
        value: backendProcessor,
      },
      ...unavailable,
    ];
  }, [processorOptions, unavailableOptions, backendProcessor]);

  // isDirty — true when form values differ from backend (order-insensitive for chips)
  const isDirty = useMemo(() => {
    if (processor !== backendProcessor) {
      return true;
    }
    const a = [...processorsList].sort();
    const b = [...backendProcessorsList].sort();
    if (a.length !== b.length) {
      return true;
    }
    return a.some((p, i) => p !== b[i]);
  }, [processor, processorsList, backendProcessor, backendProcessorsList]);

  // Prefill processor + processorsList whenever type/channel/currency/target changes
  useEffect(() => {
    setProcessor(backendProcessor);
    setProcessorsList(backendProcessorsList);
  }, [backendProcessor, backendProcessorsList]);

  // Reset type + channel to defaults when a different currency opens the modal
  useEffect(() => {
    setTransactionType(AfriexTransactionType.DEPOSIT);
    setTransactionChannel(AfriexTransactionChannel.BANK_ACCOUNT);
  }, [currency?.id]);

  useEffect(() => {
    setTransactionType(AfriexTransactionType.DEPOSIT);
  }, [processors]);

  // Keep the pending addition inside the options on screen. Also runs on every
  // corridor change, since the options change with the type and channel and a
  // value held over from the previous corridor may no longer be selectable.
  useEffect(() => {
    setProcessorToAdd((current) =>
      processorOptions.some((option) => option.value === current)
        ? current
        : processorOptions[0]?.value,
    );
  }, [processorOptions]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-5/6 md:w-4/6 lg:w-6/12 xl:w-5/12 2xl:w-4/12"
      closeOnOutsideClick
      title={`Choose Default Processor for ${currency?.name || currency?.code}`}
    >
      {isLoading ? (
        <Spinner size={8} className="text-cyan-600" />
      ) : (
        <form onSubmit={handleSubmit} className="px-0 md:px-5">
          <div className="mt-3">
            <Select
              options={getProcessorTxTypeOptions()}
              value={transactionType}
              label="Transaction Type"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setTransactionType(e?.target?.value as AfriexTransactionType)
              }
              placeholder="Select One"
            />
          </div>
          <div className="mt-3">
            <Select
              options={getTransactionChannelsOptions(excludedChannels)}
              value={transactionChannel}
              label="Transaction Channel"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setTransactionChannel(
                  e?.target?.value as AfriexTransactionChannel,
                )
              }
              placeholder="Select One"
            />
          </div>
          <div className="mt-5">
            <Select
              options={currentProcessorOptions}
              label="Current Processor"
              value={processor}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setProcessor(e?.target?.value as AfriexTransactionProcessor)
              }
              placeholder="Select One"
            />
          </div>

          <div className="mt-5">
            <label className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-200">
              Active Available Processor(s)
            </label>
            <ul className="flex flex-row flex-wrap gap-2 mb-2 mt-1">
              {processorsList.map((p) => (
                <li
                  key={p}
                  className="flex items-center gap-2 p-1 px-2 rounded bg-indigo-300 text-sm dark:bg-indigo-900 dark:text-white"
                >
                  {p}
                  <XIcon
                    className="w-4 h-4 text-red-500 cursor-pointer"
                    onClick={() => handleRemoveFromList(p)}
                  />
                </li>
              ))}
            </ul>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  options={[...processorOptions, ...unavailableOptions]}
                  value={processorToAdd}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setProcessorToAdd(
                      e?.target?.value as AfriexTransactionProcessor,
                    )
                  }
                  placeholder="Select One"
                />
              </div>
              <Button
                type="button"
                variant="solid"
                colorScheme="cyan"
                className="w-fit"
                onClick={handleAddToList}
                disabled={!canAddProcessor}
              >
                Add <PlusIcon className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-8 mb-3">
            <Button
              onClick={onClose}
              variant="outline"
              colorScheme="cyan"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="solid"
              colorScheme="cyan"
              disabled={isLoading || !isDirty}
            >
              Submit
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default UpdateProcessorModal;
